import { useState, useCallback, useEffect, useRef } from 'react';
import { transformData } from '../../../utils/data-transform';
import { ms2Regions } from '../config/layers';

/**
 * Custom hook for managing layer data and operations
 */
const useLayerData = ({ apiClient, apiType, headers = {}, mapRef, isAuthenticated, setShowAuthDialog }) => {
  // Layer state
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [layerModels, setLayerModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hasMoreModels, setHasMoreModels] = useState(false);
  const [modelPage, setModelPage] = useState(1);
  const [totalModels, setTotalModels] = useState(0);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [searchData, setSearchData] = useState({});
  const authenticated = useRef(isAuthenticated);

  // Memoize headers to ensure stability
  const currentHeaders = useRef(headers);

  // Debug headers when they change
  useEffect(() => {
    if (headers && Object.keys(headers).length > 0) {
      // console.log('🔄 Updating internal currentHeaders:', headers);
      currentHeaders.current = headers;
      authenticated.current = isAuthenticated;
    }
  }, [headers, isAuthenticated]);

  // Fetch layer models
  const fetchLayerModels = useCallback(async (searchBy, searchValue, page = 1) => {
    if (!searchBy) return;
    try {
      setLoadingModels(true);
      let value = searchValue;
      try {
        value = JSON.parse(searchValue);
        if (Array.isArray(value)) {
          searchValue = value.join(',');
        }
      } catch (e) {
        // DO NOTHING
      }
      
      const requestData = {
        query: { key: searchBy, value: searchValue },
        page: page,
        size: 10,
        count: totalModels || 0
      };

      // console.log('Headers in fetchLayerModels before request ::', currentHeaders.current);

      const response = await apiClient.postRequest(
        '/keyword/search',
        requestData,
        apiType,
        currentHeaders.current
      );

      if (response.pageinfo.count === 0) {
        setLayerModels([]);
        setHasMoreModels(false);
        setTotalModels(0);
      } else {
        const transformedData = transformData(response);
        setTotalModels(response.pageinfo.count);

        const currentModelsCount = page === 1 ? 0 : layerModels.length;
        const newTotalLoaded = currentModelsCount + transformedData.length;
        const hasMore = newTotalLoaded < response.pageinfo.count;

        if (page === 1) {
          setLayerModels(transformedData);
        } else {
          setLayerModels(prev => [...prev, ...transformedData]);
        }
        setHasMoreModels(hasMore);
      }
    } catch (error) {
      console.error('Error fetching layer models:', error);
    } finally {
      setLoadingModels(false);
    }
  }, [apiClient, apiType, layerModels, totalModels]);

  // Add layer event listeners
  const addLayerEventListeners = useCallback((map) => {
    let hoveredCountyId = null;
    let selectedCountyId = null;

    map.off('click', 'dmqt-layer-boundaries-fill');

    map.on('mousemove', 'dmqt-layer-boundaries-fill', (e) => {
      map.getCanvas().style.cursor = 'pointer';

      if (e.features.length > 0) {
        if (hoveredCountyId !== null && hoveredCountyId !== selectedCountyId) {
          map.setFeatureState(
            { source: 'dmqt-layer-boundaries', id: hoveredCountyId },
            { hover: false }
          );
        }

        hoveredCountyId = e.features[0].id;
        if (hoveredCountyId !== selectedCountyId) {
          map.setFeatureState(
            { source: 'dmqt-layer-boundaries', id: hoveredCountyId },
            { hover: true }
          );
        }
      }
    });

    map.on('mouseleave', 'dmqt-layer-boundaries-fill', () => {
      map.getCanvas().style.cursor = '';
      if (hoveredCountyId !== null && hoveredCountyId !== selectedCountyId) {
        map.setFeatureState(
          { source: 'dmqt-layer-boundaries', id: hoveredCountyId },
          { hover: false }
        );
      }
      hoveredCountyId = null;
    });

    map.on('click', 'dmqt-layer-boundaries-fill', (e) => {
      if (!e.features.length) return;

      if(!authenticated.current){
        setShowAuthDialog(true);
        return;
      }

      // Reset previous selected county
      if (selectedCountyId !== null) {
        map.setFeatureState(
          { source: 'dmqt-layer-boundaries', id: selectedCountyId },
          { selected: false }
        );
      }

      const feature = e.features[0];
      selectedCountyId = feature.id;

      const searchBy = feature.properties['ref_key'];
      const searchValue = feature.properties['ref_value'];
      let displayName = feature.properties['display_name'];
      const region = ms2Regions.find(region => region.id == searchBy);
      // console.log('🔄 Region:', region);
      if(region){
        displayName = region.name;
      }

      // Set new selected county
      map.setFeatureState(
        { source: 'dmqt-layer-boundaries', id: selectedCountyId },
        { selected: true }
      );
      setSearchData({ key: searchBy, value: searchValue });
      setSelectedLayer(displayName);
      setIsPanelVisible(true);
      setModelPage(1);
      setLayerModels([]);
      setHasMoreModels(false);
      setTotalModels(0);

      fetchLayerModels(searchBy, searchValue, 1);
    });
  }, [fetchLayerModels]);

  // Add layer layers
  const addLayerLayers = useCallback((layer, geojsonData) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Update or add the county boundaries source
    if (map.getSource('dmqt-layer-boundaries')) {
      map.getSource('dmqt-layer-boundaries').setData(geojsonData);
    } else {
      map.addSource('dmqt-layer-boundaries', {
        type: 'geojson',
        data: geojsonData,
        generateId: true
      });
    }

    // Define layer configurations
    const layers = [
      {
        id: 'dmqt-layer-boundaries-fill',
        type: 'fill',
        source: 'dmqt-layer-boundaries',
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#0EA5E9', // Selected - Bright ocean blue
            ['all', ['has', 'has_models'], ['get', 'has_models']],
            '#0369A1', // Counties with models - Ocean blue
            ['has', 'ref_key'], [
              'match',
              ['get', 'ref_key'],
              'rbfs_west', '#0369A1', // RBFS West - Ocean blue (same as counties)
              'rbfs_east', '#0369A1', // RBFS East - Ocean blue (same as counties)
              'ms2', '#0369A1',       // MS2 - Ocean blue (same as counties)
              '#0369A1' // Default for other regions - Ocean blue
            ],
            '#94A3B8' // Default fallback - Cool gray (for counties without models)
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], 0.85,
            ['boolean', ['feature-state', 'hover'], false], 0.75,
            ['all', ['has', 'has_models'], ['get', 'has_models']], 0.7,
            ['has', 'ref_key'], 0.7,
            0.2 // Default opacity
          ]
        }
      },
      {
        id: 'dmqt-layer-boundaries-lines',
        type: 'line',
        source: 'dmqt-layer-boundaries',
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#38BDF8',  // Selected border - Lighter ocean blue for contrast
            ['all', ['has', 'has_models'], ['get', 'has_models']],
            '#075985',  // Counties with models - Darker ocean blue
            ['has', 'ref_key'], [
              'match',
              ['get', 'ref_key'],
              'rbfs_west', '#075985', // RBFS West - Darker ocean blue (same as counties)
              'rbfs_east', '#075985', // RBFS East - Darker ocean blue (same as counties)
              'ms2', '#075985',       // MS2 - Darker ocean blue (same as counties)
              '#075985' // Darker ocean blue for other regions
            ],
            '#64748B'   // Default border - Darker cool gray
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], 2.5,
            ['boolean', ['feature-state', 'hover'], false], 1.75,
            ['all', ['has', 'has_models'], ['get', 'has_models']], 1.5,
            ['has', 'ref_key'], 1.5,
            1 // Default width
          ],
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], 1,
            ['boolean', ['feature-state', 'hover'], false], 0.9,
            ['all', ['has', 'has_models'], ['get', 'has_models']], 0.9,
            ['has', 'ref_key'], 0.9,
            0.7 // Default opacity
          ]
        }
      }
    ];

    // Add or update layers
    layers.forEach(layer => {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer);
      }
    });

    // Add event listeners if they haven't been added yet
    if (!map._countyEventListenersAdded || map._eventHandlerVersion !== fetchLayerModels) {
      addLayerEventListeners(map);
      map._countyEventListenersAdded = true;
      map._eventHandlerVersion = fetchLayerModels; // Track the last used reference
    }
  }, [addLayerEventListeners, mapRef]);

  // Fetch layer data
  const fetchLayerData = useCallback(async (layer) => {
    // console.log('Fetching layer data:', layer);
    try {
      let response;
      if (layer.id === 'counties') {
        const map = mapRef.current?.getMap();
        if (!map) throw new Error('Map not initialized');

        // Get the base counties data from the existing source
        const baseCountiesSource = map.getSource('base-counties');
        if (!baseCountiesSource) throw new Error('Base counties source not found');

        const baseCountiesData = baseCountiesSource._data;

        // Fetch only the models data
        const modelsData = await apiClient.getRequest('/counties/with-models', apiType, currentHeaders);

        // Merge the data
        const mergedFeatures = baseCountiesData.features.map(feature => {
          const countyName = feature.properties.cnty_nm;
          const hasModels = modelsData.some(model => model.toLocaleLowerCase() === countyName.toLocaleLowerCase());
          if(hasModels){
            return {
              ...feature,
              properties: {
                ...feature.properties,
                has_models: hasModels,
                ref_key: 'county_name',
                ref_value: countyName,
                display_name: countyName + ' County'
              }
            };
          } else {
            return null;
          }
        }).filter(Boolean);

        response = {
          type: 'FeatureCollection',
          features: mergedFeatures
        };
      } else {
        response = await apiClient.getRequest(layer.endpoint, apiType, currentHeaders.current);
      }

      if (!response) {
        throw new Error('No response from API');
      }

      const geojsonData = response;

      if (!geojsonData || !geojsonData.features || !Array.isArray(geojsonData.features)) {
        throw new Error('Invalid response format: expected a GeoJSON FeatureCollection');
      }

      addLayerLayers(layer, geojsonData);
      return geojsonData;
    } catch (error) {
      console.error('Error fetching layer data:', error);
    }
  }, [apiClient, apiType, addLayerLayers]);

  // Load more layer models
  const loadMoreLayerModels = useCallback(() => {
    if (loadingModels || !hasMoreModels) return;
    const nextPage = modelPage + 1;
    setModelPage(nextPage);
    fetchLayerModels(searchData.key, searchData.value, nextPage);
  }, [loadingModels, hasMoreModels, modelPage, searchData, fetchLayerModels]);

  return {
    selectedLayer,
    setSelectedLayer,
    layerModels,
    loadingModels,
    hasMoreModels,
    modelPage,
    totalModels,
    isPanelVisible,
    setIsPanelVisible,
    fetchLayerModels,
    fetchLayerData,
    loadMoreLayerModels,
    addLayerLayers
  };
};

export default useLayerData;
